import React, { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, Eye, EyeOff, LoaderCircle, Lock, LockOpen, PencilLine, Trash2, X } from 'lucide-react';

import './SceneAssetInspector.css';

const numberValue = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;

const feedbackIcon = {
  error: AlertCircle,
  idle: CheckCircle2,
  pending: PencilLine,
  saving: LoaderCircle,
  success: CheckCircle2,
};

/** Keyboard-accessible companion to the canvas transformer. This BattleRoom
 * table-chrome surface is the precise editing path for touch and keyboard users.
 * date_of_change: 2026-09-11 */
const SceneAssetInspector = ({ anchorRef, asset, onChange, onDelete, onClose }) => {
  const [draft, setDraft] = useState(asset);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ tone: 'idle', text: 'All changes saved.' });
  const [anchorBounds, setAnchorBounds] = useState(null);

  useEffect(() => setDraft(asset), [asset]);
  useEffect(() => setFeedback({ tone: 'idle', text: 'All changes saved.' }), [asset?.assetInstanceId]);

  useLayoutEffect(() => {
    const anchor = anchorRef?.current;
    if (!anchor || typeof window === 'undefined') return undefined;

    const updateBounds = () => {
      const rect = anchor.getBoundingClientRect();
      const narrow = window.innerWidth <= 720;
      setAnchorBounds({
        top: Math.max(8, rect.top + (narrow ? 52 : 12)),
        right: narrow ? 8 : Math.max(12, window.innerWidth - rect.right + 12),
        bottom: Math.max(8, window.innerHeight - rect.bottom + (narrow ? 48 : 12)),
        maxHeight: Math.max(120, rect.height - 24),
      });
    };

    updateBounds();
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateBounds);
    resizeObserver?.observe(anchor);
    window.addEventListener('resize', updateBounds);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateBounds);
    };
  }, [anchorRef]);

  if (!asset || !draft) return null;

  const setField = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setFeedback({ tone: 'pending', text: 'Unsaved changes.' });
  };
  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFeedback({ tone: 'saving', text: 'Saving scene image…' });
    try {
      const updatedAsset = await onChange({
        name: String(draft.name || '').trim(),
        layer: draft.layer,
        x: numberValue(draft.x),
        y: numberValue(draft.y),
        width: numberValue(draft.width),
        height: numberValue(draft.height),
        rotation: numberValue(draft.rotation),
        opacity: numberValue(draft.opacity),
        locked: draft.locked === true,
        visible: draft.visible !== false,
      });
      if (!updatedAsset) throw new Error('The scene image update was not confirmed.');
      setDraft(updatedAsset);
      setFeedback({ tone: 'success', text: 'Changes saved.' });
    } catch (error) {
      // Keep the precision editor usable and explain the recovery path when the
      // authoritative scene rejects an edit. date_of_change: 2026-09-11
      setFeedback({ tone: 'error', text: error?.message || 'Changes were not saved. Try again.' });
    } finally {
      setSaving(false);
    }
  };

  const FeedbackIcon = feedbackIcon[feedback.tone] || CheckCircle2;

  const inspector = (
    <aside
      className="br-scene-asset-inspector"
      aria-label={`Scene image properties: ${asset.name}`}
      style={anchorBounds ? {
        '--scene-inspector-top': `${anchorBounds.top}px`,
        '--scene-inspector-right': `${anchorBounds.right}px`,
        '--scene-inspector-bottom': `${anchorBounds.bottom}px`,
        '--scene-inspector-max-height': `${anchorBounds.maxHeight}px`,
      } : undefined}
    >
      <div className="br-scene-asset-inspector__header">
        <div>
          <span>Scene image</span>
          <h2>{asset.name}</h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Close scene image properties"><X size={17} /></button>
      </div>
      <form onSubmit={save} autoComplete="off">
        <label className="br-scene-asset-inspector__wide">
          <span>Name</span>
          <input required value={draft.name} maxLength={80} onChange={(event) => setField('name', event.target.value)} />
        </label>
        <label className="br-scene-asset-inspector__wide">
          <span>Layer</span>
          <select value={draft.layer} onChange={(event) => setField('layer', event.target.value)}>
            <option value="token">Token Layer</option>
            <option value="gamemaster">GM Layer</option>
          </select>
        </label>
        {['x', 'y', 'width', 'height', 'rotation'].map((field) => (
          <label key={field}>
            <span>{field === 'rotation' ? 'Rotation' : field.toUpperCase()}</span>
            <input type="number" step="0.1" min={field === 'width' || field === 'height' ? 1 : undefined} value={draft[field]} onChange={(event) => setField(field, event.target.value)} />
          </label>
        ))}
        <label>
          <span>Opacity</span>
          <input type="number" step="0.05" min="0" max="1" value={draft.opacity} onChange={(event) => setField('opacity', event.target.value)} />
        </label>
        <div className="br-scene-asset-inspector__toggles">
          <button type="button" aria-pressed={draft.visible !== false} onClick={() => setField('visible', draft.visible === false)}>
            {draft.visible === false ? <EyeOff size={16} /> : <Eye size={16} />}
            {draft.visible === false ? 'Hidden' : 'Visible'}
          </button>
          <button type="button" aria-pressed={draft.locked === true} onClick={() => setField('locked', draft.locked !== true)}>
            {draft.locked ? <Lock size={16} /> : <LockOpen size={16} />}
            {draft.locked ? 'Locked' : 'Unlocked'}
          </button>
        </div>
        <div className="br-scene-asset-inspector__commit">
          <div
            id="scene-asset-save-status"
            className={`br-scene-asset-inspector__feedback is-${feedback.tone}`}
            role={feedback.tone === 'error' ? 'alert' : 'status'}
            aria-live="polite"
          >
            <FeedbackIcon size={15} aria-hidden="true" />
            <span>{feedback.text}</span>
          </div>
          <div className="br-scene-asset-inspector__actions">
            <button type="button" className="br-scene-asset-inspector__delete" onClick={onDelete}><Trash2 size={16} /> Remove</button>
            <button type="submit" className="br-scene-asset-inspector__save" disabled={saving} aria-describedby="scene-asset-save-status">{saving ? 'Saving…' : 'Save changes'}</button>
          </div>
        </div>
      </form>
    </aside>
  );

  // The map viewport can slide under compact sidebars. Portal precision editing
  // chrome to the document overlay layer so it stays inside the visible viewport
  // without changing the canvas coordinate system. date_of_change: 2026-09-11
  return typeof document === 'undefined' ? inspector : createPortal(inspector, document.body);
};

export default SceneAssetInspector;
